import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import config from '../config/network';
import CoreABI from '../abi/ClusterCore.json';
import ERC20ABI from '../abi/ERC20.json';
import { getErrorMessage } from '../utils/helpers';

export function useCore() {
  const { provider, signer, account } = useWeb3();

  function getCoreRead() {
    if (!provider) return null;
    return new ethers.Contract(config.contracts.core, CoreABI, provider);
  }

  function getCoreWrite() {
    if (!signer) return null;
    return new ethers.Contract(config.contracts.core, CoreABI, signer);
  }

  async function getUSDTAddress() {
    // Use config.usdt first (fastest), fallback to on-chain
    if (config.usdt && config.usdt !== '0x0000000000000000000000000000000000000000') {
      return config.usdt;
    }
    const core = getCoreRead();
    if (!core) return null;
    return await core.usdt();
  }

  async function getUSDTContract() {
    const usdtAddr = await getUSDTAddress();
    if (!usdtAddr || !signer) return null;
    return new ethers.Contract(usdtAddr, ERC20ABI, signer);
  }

  async function approveUSDT(amount) {
    try {
      const usdtAddr = await getUSDTAddress();
      if (!usdtAddr) throw new Error('USDT address not found');
      if (!signer) throw new Error('Wallet not connected');

      const usdtRead = new ethers.Contract(usdtAddr, ERC20ABI, provider);
      const usdtWrite = new ethers.Contract(usdtAddr, ERC20ABI, signer);
      const spender = config.contracts.core;

      // Check current allowance
      const currentAllowance = await usdtRead.allowance(account, spender);
      if (currentAllowance.gte(amount)) return { success: true, skipped: true };

      // Some USDT tokens require resetting allowance to 0 first
      if (currentAllowance.gt(0)) {
        const resetTx = await usdtWrite.approve(spender, 0);
        await resetTx.wait();
      }

      // Approve exact large amount (some tokens don't support MaxUint256)
      const approveAmount = ethers.utils.parseUnits('999999999', 18);
      const tx = await usdtWrite.approve(spender, approveAmount);
      await tx.wait();

      // Verify allowance was set
      const newAllowance = await usdtRead.allowance(account, spender);
      if (!newAllowance.gte(amount)) {
        throw new Error('Approval failed - please try again');
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  async function register(referrerId, notify) {
    try {
      const core = getCoreRead();
      const prices = await core.getPackagePrices();
      const price = prices[0];

      if (notify) notify('Approving USDT...', 'pending', 0);
      const approveResult = await approveUSDT(price);
      if (!approveResult.success) throw new Error(approveResult.error);

      if (notify) notify('Registering...', 'pending', 0);
      const coreWrite = getCoreWrite();
      const tx = await coreWrite.register(referrerId || 0);
      await tx.wait();
      return { success: true, tx };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  async function registerMultiple(referrerId, upToPackageId, notify) {
    try {
      const core = getCoreRead();
      const prices = await core.getPackagePrices();
      let total = ethers.BigNumber.from(0);
      for (let i = 0; i <= upToPackageId; i++) total = total.add(prices[i]);

      if (notify) notify('Approving USDT...', 'pending', 0);
      const approveResult = await approveUSDT(total);
      if (!approveResult.success) throw new Error(approveResult.error);

      if (notify) notify('Registering...', 'pending', 0);
      const coreWrite = getCoreWrite();
      const tx = await coreWrite.registerMultiplePackages(referrerId || 0, upToPackageId);
      await tx.wait();
      return { success: true, tx };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  async function activatePackage(packageId, notify) {
    try {
      const core = getCoreRead();
      const prices = await core.getPackagePrices();
      const price = prices[packageId];

      if (notify) notify('Approving USDT...', 'pending', 0);
      const approveResult = await approveUSDT(price);
      if (!approveResult.success) throw new Error(approveResult.error);

      if (notify) notify('Activating package...', 'pending', 0);
      const coreWrite = getCoreWrite();
      const tx = await coreWrite.activatePackage(packageId);
      await tx.wait();
      return { success: true, tx };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  async function activateMultiplePackages(fromId, toId, notify) {
    try {
      const core = getCoreRead();
      const prices = await core.getPackagePrices();
      let total = ethers.BigNumber.from(0);
      for (let i = fromId; i <= toId; i++) total = total.add(prices[i]);

      if (notify) notify('Approving USDT...', 'pending', 0);
      const approveResult = await approveUSDT(total);
      if (!approveResult.success) throw new Error(approveResult.error);

      if (notify) notify('Activating packages...', 'pending', 0);
      const coreWrite = getCoreWrite();
      const tx = await coreWrite.activateMultiplePackages(fromId, toId);
      await tx.wait();
      return { success: true, tx };
    } catch (err) {
      return { success: false, error: getErrorMessage(err) };
    }
  }

  async function getUserInfo(uid) {
    try {
      const core = getCoreRead();
      if (!core) return null;
      const info = await core.getUserInfo(uid);
      return {
        wallet: info.wallet,
        referrerId: info.referrerId.toString(),
        joinTime: info.joinTime.toString(),
        packageActive: info.packageActive,
        totalDirectRefs: info.totalDirectRefs.toString(),
        totalIncomeEarned: info.totalIncomeEarned,
        userType: info.userType,
      };
    } catch {
      return null;
    }
  }

  async function getIncomeBreakdown(uid) {
    try {
      const core = getCoreRead();
      if (!core) return null;
      const b = await core.getUserIncomeBreakdown(uid);
      return { boardIncome: b.boardIncome, directIncome: b.directIncome, levelIncome: b.levelIncome };
    } catch {
      return null;
    }
  }

  async function getDirectReferrals(uid) {
    try {
      const core = getCoreRead();
      if (!core) return [];
      const refs = await core.getDirectReferrals(uid);
      return refs.map(r => r.toString());
    } catch {
      return [];
    }
  }

  async function getDirectReferralsByRange(uid, offset, limit) {
    try {
      const core = getCoreRead();
      if (!core) return { refs: [], total: 0 };
      const result = await core.getDirectReferralsByRange(uid, offset, limit);
      return { refs: result.refs.map(r => r.toString()), total: result.total.toString() };
    } catch {
      return { refs: [], total: 0 };
    }
  }

  async function getTotalUsers() {
    try {
      const core = getCoreRead();
      if (!core) return '0';
      const t = await core.getTotalUsers();
      return t.toString();
    } catch {
      return '0';
    }
  }

  async function getPackagePrices() {
    try {
      const core = getCoreRead();
      if (!core) return [];
      return await core.getPackagePrices();
    } catch {
      return [];
    }
  }

  async function getActiveDirectsForPackage(uid, pkgId) {
    try {
      const core = getCoreRead();
      if (!core) return '0';
      const c = await core.getActiveDirectsForPackage(uid, pkgId);
      return c.toString();
    } catch {
      return '0';
    }
  }

  async function getUSDTBalance() {
    try {
      if (!provider || !account) return null;
      const usdtAddr = await getUSDTAddress();
      if (!usdtAddr) return null;
      const usdt = new ethers.Contract(usdtAddr, ERC20ABI, provider);
      return await usdt.balanceOf(account);
    } catch {
      return null;
    }
  }

  async function getLevelMatrixLevelCounts(uid, packageId) {
    try {
      const core = getCoreRead();
      if (!core) return [];
      const counts = await core.getLevelMatrixLevelCounts(uid, packageId);
      return counts.map(c => c.toNumber());
    } catch {
      return Array(15).fill(0);
    }
  }

  async function getLevelMatrixLevelUsers(uid, packageId, level) {
    try {
      const core = getCoreRead();
      if (!core) return [];
      const users = await core.getLevelMatrixLevelUsers(uid, packageId, level);
      return users.map(u => u.toString());
    } catch {
      return [];
    }
  }

  async function getLevelMatrixInfo(uid, packageId) {
    try {
      const core = getCoreRead();
      if (!core) return null;
      const info = await core.getLevelMatrixInfo(uid, packageId);
      return {
        upline: info.upline.toString(),
        downlines: info.downlines.map(d => d.toString()),
        teamSize: info.teamSize.toString(),
      };
    } catch {
      return null;
    }
  }

  return {
    getCoreRead, getCoreWrite, getUSDTContract,
    register, registerMultiple,
    activatePackage, activateMultiplePackages,
    getUserInfo, getIncomeBreakdown, getDirectReferrals, getDirectReferralsByRange,
    getTotalUsers, getPackagePrices, getActiveDirectsForPackage, getUSDTBalance,
    approveUSDT, getLevelMatrixLevelCounts, getLevelMatrixLevelUsers, getLevelMatrixInfo,
  };
}
